import {
  createColumnHelper,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";

import {
  Avatar,
  AvatarFallback,
  Body,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Heading,
  Icon,
  TableData,
} from "@elaniintc/goes-design-system";
import TablePaginantion from "@/components/common/table/pagination";
import type { FormalityRequestType } from "@/types/entities/formality-request";
import dayjs from "dayjs";
import { getFormattedElapsedDaysWithColor } from "@/utils/dates";
import { useEffect, useMemo, useState } from "react";
import { usePageSortFilters } from "@/hooks/use-page-fs";
import { filterFormalityRequests, sortFormalityRequests } from "../../utils";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import { NavLink } from "react-router";
import RequestStatus from "@/pages/home/request-state";
import type { RefName } from "@/workflow_api/types/core";

const columnHelper = createColumnHelper<FormalityRequestType>();

const createColumns = () =>
  [
    columnHelper.accessor("name", {
      id: "name",
      header: "Nombre del trámite",
      sortUndefined: "last",
      meta: {
        className:
          "max-w-[80px] md:max-w-[175px] sm:max-w-[250px] text-ellipsis overflow-hidden font-semibold",
      },
    }),
    columnHelper.accessor("code", {
      header: "Número ID",
      sortUndefined: "last",
      meta: {
        className: "sm:max-w-none text-ellipsis overflow-hidden",
      },
      cell: (info) => {
        return info.getValue();
      },
    }),
    columnHelper.accessor("status", {
      header: "Estado",
      sortUndefined: "last",
      meta: {
        className: "hidden sm:table-cell",
      },
      cell: (info) => {
        return (
          <RequestStatus
            node_name={
              info.row.original.current_node?.display_name || "Desconocido"
            }
            node_type={
              (info.row.original.current_node?.ref_name as RefName) ||
              "resolution-node"
            }
            status={info.row.original.status || "cancelled"}
            status_label={info.row.original.status_label || ""}
            flow_status={info.row.original.flow_status}
          />
        );
      },
    }),

    columnHelper.accessor((row) => row?.applicant?.first_name, {
      header: "Usuario Solicitante",
      id: "applicant",
      meta: {
        className: "hidden lg:table-cell",
      },
      cell: (info) => {
        const applicant = info.row.original.applicant;
        if (!applicant?.first_name) {
          return <p>&nbsp;</p>;
        }
        const initials = applicant.first_name.charAt(0) ?? "U";
        return (
          <div className="flex items-center gap-4">
            <Avatar size="xs">
              <AvatarFallback color="action">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex flex-col gap-0">
              <span className="text-body font-normal">
                {`${applicant.first_name ?? ""} ${applicant.last_name ?? ""}`.trim()}
              </span>
              <span className="text-body-quiet font-normal text-xs">
                {applicant?.email || ""}
              </span>
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor("created_at", {
      id: "elapsed_days",
      header: "Inicio y Tiempo transcurrido",
      meta: {
        className: "max-w-[180px] hidden xs:table-cell ",
        headerClassName: "truncate ellipsis",
      },
      sortUndefined: "first",
      cell: (row) => {
        const creationDate = row.getValue();
        if (!creationDate) {
          return <p>&nbsp;</p>;
        }
        const { formatted, colorClass } =
          getFormattedElapsedDaysWithColor(creationDate);
        const status = row.row?.original?.status?.toLowerCase();
        const shouldShowBadge =
          status === "can_extended" || status === "time_expired";
        const badgeColor = status === "can_extended" ? "warning" : "danger";

        const date = new Date(creationDate);
        return (
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Icon
                type="far"
                name="calendar"
                size="lg"
                className={`${colorClass}-quiet`}
              />
              <div className="pl-1">
                <p className={`contextual-${colorClass}`}>
                  {dayjs(date).format("DD/MM/YY")}
                </p>
                <p className={`text-size-body-xs contextual-${colorClass}`}>
                  {formatted}
                </p>
              </div>
            </div>
            {shouldShowBadge && (
              <Avatar size="xs" className="mr-2">
                <AvatarFallback
                  color={badgeColor}
                  className="flex items-center justify-center"
                >
                  <Icon
                    type="fas"
                    name="clock"
                    className={`text-${badgeColor}-quiet`}
                    size="sm"
                  />
                </AvatarFallback>
              </Avatar>
            )}
          </div>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      meta: {
        className: "w-0 pr-2",
      },
      cell: (row) => {
        return <Menu id={row.row.original.id} />;
      },
    }),
  ] as ColumnDef<FormalityRequestType>[];

const Menu = ({ id }: { id: string }) => {
  const { hasPermission } = useAuthorization();
  const canReadRequests = hasPermission(
    Permissions.expedient_base_registries["read-formality"],
  );

  if (!canReadRequests) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="xs" className="size-8">
          <Icon
            type="far"
            name="ellipsis-vertical"
            className="text-current text-sm"
          />
          <span className="sr-only">Acciones para la solicitud {id}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-55.5 min-w-56 rounded-lg border-neutral-silent text-body font-normal"
        side="bottom"
        align="end"
        sideOffset={4}
      >
        <DropdownMenuGroup>
          {canReadRequests && (
            <DropdownMenuItem
              className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
              asChild
            >
              <NavLink to={`tramite/${id}`} className="w-full h-full block">
                Detalle
              </NavLink>
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface DigitalRequestTableProps {
  searchString?: string;
  requestList?: FormalityRequestType[];
  queryFilters?: object;
}

const DigitalRequestTable = ({
  searchString,
  requestList,
  queryFilters,
}: DigitalRequestTableProps) => {
  const isLoading = false; //manage loading state
  const { setSearchInput } = usePageSortFilters();
  const [sorting, setSorting] = useState<SortingState>([]);

  useEffect(() => {
    setSearchInput(searchString ?? "");
  }, [searchString, setSearchInput]);

  const columns = useMemo(() => createColumns(), []);

  const filteredRequests = useMemo(() => {
    let filtered = filterFormalityRequests(
      requestList ?? [],
      searchString,
      queryFilters,
    );

    // Apply sorting
    if (sorting.length > 0) {
      const { id, desc } = sorting[0];
      filtered = sortFormalityRequests(filtered, id, desc);
    }

    return filtered;
  }, [requestList, searchString, sorting, queryFilters]);

  if (!isLoading && (requestList?.length ?? 0) === 0) {
    return (
      <div className="flex flex-col flex-auto justify-center items-center border-t border-neutral-quiet py-10 gap-2">
        <Heading variant="h5" color="quiet">
          Sin solicitudes creadas
        </Heading>
        <Body color="quiet" className="text-center">
          Las solicitudes aparecerán aquí tan pronto sean creadas
        </Body>
      </div>
    );
  }

  return (
    <>
      <TableData
        columns={columns}
        isFirstLoading={isLoading}
        data={filteredRequests}
        stripped
        withInnerBorder={false}
        emptyState={<span></span>}
        wrapperClassName="flex-auto"
        sorting={sorting}
        onSortingChange={setSorting}
      />
      <div className="p-3">
        <TablePaginantion
          totalPages={1}
          totalItems={filteredRequests.length}
          pageSize={filteredRequests.length}
          currentPage={1}
          hasNext={false}
          hasPrevious={false}
          onPageChange={() => {}}
          onNext={() => {}}
          onPrevious={() => {}}
        />
      </div>
    </>
  );
};

export { DigitalRequestTable };
